import React from "react";
import CardDefault from '../../../../../../assets/games/russianPoker/rp_sprite_card.webp';
import riverBackCard from "../../../../../../assets/games/russianPoker/river_sprite_card.webp";

import selectCard from "../../../../../../assets/sounds/selectcard.mp3";
import unselectCard from "../../../../../../assets/sounds/unselectcard.mp3";
import cardsdropping_snd from "../../../../../../assets/sounds/carddroping.mp3";
import cardsshuffle from "../../../../../../assets/sounds/shuffle-cards.mp3";
import gsap from "gsap";
import { Howl, Howler } from "howler";

// Back-of-card frame — also the safe fallback if `this.props.frame`
// ever comes back undefined (e.g. a card text that doesn't have an
// exact key in mapCards). Prevents "cardFrame is not iterable" crashes.
const DEFAULT_FRAME = [689, 0, 53, 73];
const safeFrame = (frame) => (Array.isArray(frame) && frame.length === 4 ? frame : DEFAULT_FRAME);

// Same motion language as dealerCard.js — kept identical so the player's
// hand and the dealer's hand move consistently with each other.
const EASE_LIFT = "power1.in";
const EASE_FLIGHT = "power2.out";
const EASE_SETTLE = "back.out(.35)";
const GPU = { force3D: true };

// How far a card lifts when marked for the draw exchange. Named so every
// place that sets this card's y (CardPullUP, changeCardPosition) agrees on
// the same value — previously changeCardPosition didn't know about the
// offset at all and would silently cancel it (see comment there).
const SELECT_LIFT_Y = 20;

// Opacity a card dulls down to while marked for the draw exchange. Used
// both by the GSAP tween in CardPullUP (so the dip happens immediately on
// tap) and by render()'s style.opacity below (so the dulled look survives
// every later re-render/position update instead of only lasting until the
// next changeCardPosition tween runs).
const SELECTED_OPACITY = .8;

/**
 * CSS/DOM replacement for the old <Sprite> (react-konva) based Card.
 *
 * WHY THIS EXISTS (read before "fixing" it back to canvas):
 * The old Card lived inside a Konva <Layer>. Every GSAP tween that moved,
 * scaled or rotated the card forced Konva to redraw + re-rasterize the
 * WHOLE layer on every animation frame (layer.batchDraw()). On iOS
 * WKWebView that repeated canvas rasterization is exactly the kind of
 * sustained memory/GPU pressure that trips the WebContent jetsam killer
 * during win sequences (portrait mode, many cards animating together).
 *
 * A CSS transform (`translate3d/scale/rotate`) on a real DOM node is
 * instead promoted to its own GPU compositor layer. The browser animates
 * it without ever touching JS/canvas memory again after the first paint.
 * Same GSAP API, same tween values (x, y, scaleX, scaleY, rotation,
 * opacity) — just a DOM element as the tween target instead of a Konva
 * node. That's why this file keeps almost the exact same method names
 * and call signatures as the original: it's a drop-in replacement for
 * cards.js, not a rewrite of the game logic.
 *
 * The sprite sheet itself is NOT split into 52 images. We keep the single
 * rp_sprite_card.png / river_sprite_card.png sheet and crop it with
 * `background-position`, which is effectively free for the browser
 * compared to Konva's per-frame canvas drawImage() crop.
 *
 * IMPORTANT — transform ownership:
 * GSAP writes directly to this node's inline `style.transform`. React's
 * render() must NEVER include a `transform` key in the style object it
 * returns, or every unrelated re-render (a GAME_STATE update, a sibling
 * prop change, anything) will stomp GSAP's current position/scale back
 * to whatever literal value render() has, silently detaching the card's
 * real hit-box from where it visually sits. The one-time resting
 * transform is instead set imperatively, once, via setCardRef below.
 */
export class Card extends React.Component {
    constructor(props) {
        super(props);
        this.logoBackSideCard = props.siteid == "rp1" ? riverBackCard : CardDefault;
        this.cardStyle = this.logoBackSideCard;

        this.state = {
            cardFrame: DEFAULT_FRAME, // back-of-card frame, same default as before
            sheetW: 0,
            sheetH: 0,
            ready: false,   // true once sheet image dims are known
            visible: false, // mirrors old Konva `visible` prop
            selected: false, // true while this card is marked for the draw exchange
        };

        this.cardDropPositionX = null;
        this.cardDropPositionY = null;
        this.cardRef = null;      // DOM node (replaces this.imageNode)
        this.cardContext = null;  // gsap.context, same as before
        this.isAnimating = false; // true while a dedicated deal/draw/reveal timeline owns this node
        this._initialTransformSet = false; // guards the one-time gsap.set below

        this.handleResize = this.handleResize.bind(this);
        this.handleResize();

        // Bound once so React doesn't tear down/remount the ref callback
        // (and re-run its guard logic) on every single render the way an
        // inline `ref={(node) => {...}}` arrow function would.
        this.setCardRef = (node) => {
            this.cardRef = node;
            if (node && !this._initialTransformSet) {
                // One-time resting transform, applied directly via GSAP so
                // React's `style` object never needs a `transform` entry.
                gsap.set(node, { x: 0, y: 0, scale: 0.15 });
                this._initialTransformSet = true;
            }
        };
    }

    componentDidMount() {
        this.logoBackSideCard = this.props.siteid == "rp1" ? riverBackCard : CardDefault;
        this.loadImage(this.logoBackSideCard);

        window.addEventListener("resize", this.handleResize);

        this.carddropsound = new Howl({ src: [cardsdropping_snd], html5: false, preload: true });
        this.selectCard = new Howl({ src: [selectCard], html5: false, preload: true });
        this.unselectCard = new Howl({ src: [unselectCard], html5: false, preload: true });
        this.cardsshuffleSound = new Howl({ src: [cardsshuffle], html5: false, preload: true });
    }

    playSound = (soundInstance) => {
        if (this.props.mutesounds) return;
        this.soundSuspendedChecker();
        if (soundInstance) {
            soundInstance.stop();
            soundInstance.play();
        }
    };

    soundSuspendedChecker = () => {
        if (document.visibilityState === 'visible' && Howler.ctx && (Howler.ctx.state === 'suspended' || Howler.ctx.state !== 'running')) {
            Howler.ctx.resume();
        }
        Howler._audioUnlocked = true;
    };

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
        if (this.image) {
            this.image.removeEventListener("load", this.handleLoad);
        }
        [this.carddropsound, this.cardsshuffleSound, this.selectCard, this.unselectCard].forEach(sound => {
            if (sound) {
                sound.stop();
                sound.unload();
            }
        });
        if (this.cardContext) {
            this.cardContext.revert();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.cardStyle !== this.props.cardStyle) {
            this.loadImage(this.props.cardStyle);
            this.cardStyle = this.props.cardStyle;
        }
        if (this.props.x !== prevProps.x) {
            this.changeCardPosition();
        }
    }

    // Same off-screen "deck" drop position math as the original, unchanged.
    handleResize() {
        if (this.cardRef) {
            gsap.set(this.cardRef, {
                x: this.props.cardsXYAProps.x,
                y: this.props.cardsXYAProps.y,
                rotation: this.props.cardsXYAProps.angle,
                scaleX: this.props.scaleX,
                scaleY: this.props.scaleY,
            });
        }

        if (
            (navigator.userAgentData && navigator.userAgentData.platform && navigator.userAgentData.platform === "Windows") ||
            (navigator.userAgent.indexOf("Windows") !== -1) ||
            (navigator.platform.includes("Mac") && !("ontouchend" in document || navigator.maxTouchPoints > 0))
        ) {
            this.cardDropPositionX = window.innerWidth / 2 + 900;
            this.cardDropPositionY = -window.innerHeight / 2 - 250;
        } else {
            if (window.innerWidth > window.innerHeight) {
                this.cardDropPositionX = window.innerWidth / 2 + 100;
                this.cardDropPositionY = -(window.innerHeight / 2) - 350;
            } else {
                this.cardDropPositionX = window.innerWidth / 2 + 250;
                this.cardDropPositionY = -(window.innerHeight / 2) - 900;
            }
        }
    }

    changeCardPosition() {
        if (!this.cardRef) return;
        // Guard against stomping on a card's own deal/draw/reveal timeline:
        // the arch recenters (props.x changes) every time a card is added,
        // which used to yank position out from under whichever card was
        // mid-flight and also never touched rotation — so already-seated
        // cards kept their OLD tilt while sliding to a NEW arch position,
        // breaking the fan every single time a card was bought/drawn.
        if (this.isAnimating) return;
        gsap.killTweensOf(this.cardRef);
        this.cardContext = gsap.context(() => {
            gsap.to(this.cardRef, {
                x: this.props.x, y: this.props.y - (this.state.selected ? SELECT_LIFT_Y : 0), rotation: this.props.angled || 0,
                duration: 0.45, ease: EASE_FLIGHT, ...GPU,
            });
        }, this.cardRef);
    }

    pullDown() {
        if (!this.cardRef) return;
        gsap.killTweensOf(this.cardRef);
        gsap.to(this.cardRef, {
            y: this.props.y + 50, x: this.props.x, opacity: 0.35, duration: 0.4, ease: EASE_FLIGHT, ...GPU,
            onComplete: () => this.setVisible(false),
        });
    }

    pullUpCards() {
        if (!this.cardRef) return;
        gsap.killTweensOf(this.cardRef);
        this.setVisible(true);
        gsap.to(this.cardRef, { y: this.props.y, x: this.props.x, opacity: 0.5, duration: 0.4, ease: EASE_FLIGHT, ...GPU });
    }

    pullUp() {
        if (!this.cardRef) return;
        gsap.killTweensOf(this.cardRef);
        gsap.to(this.cardRef, { y: this.props.y - 10, opacity: 1, duration: 0.3, ease: EASE_SETTLE, ...GPU });
    }

    showPreviousPlayercards() {
        this.props.UpdateCard({ x: this.props.x, y: this.props.y });
        if (!this.cardRef) return;

        this.isAnimating = true;
        gsap.killTweensOf(this.cardRef);
        this.setState({ cardFrame: safeFrame(this.props.frame) });
        this.setVisible(true);
        const restAngle = this.props.angled || 0;
        gsap.fromTo(
            this.cardRef,
            { x: this.cardDropPositionX, y: this.cardDropPositionY, scaleX: 0.3, scaleY: 0.3, opacity: 0.6, rotation: restAngle },
            {
                x: this.props.x, y: this.props.y, scaleX: this.props.scaleX, scaleY: this.props.scaleY,
                rotation: restAngle, opacity: 1, duration: 0.55, ease: EASE_SETTLE, ...GPU,
                onComplete: () => { this.isAnimating = false; this.afterFinish(); },
            }
        );
    }

    animateCard() {
        this.setState({ cardFrame: safeFrame(this.props.frame) });
        this.props.UpdateCard({ x: this.props.x, y: this.props.y });

        if (!this.cardRef) return;
        this.isAnimating = true;
        gsap.killTweensOf(this.cardRef);
        this.setVisible(true);
        const restAngle = this.props.angled || 0;
        gsap.fromTo(
            this.cardRef,
            { x: this.cardDropPositionX, y: this.cardDropPositionY, scaleX: 0.3, scaleY: 0.3, opacity: 0.4, rotation: restAngle },
            {
                x: this.props.x, y: this.props.y, scaleX: this.props.scaleX, scaleY: this.props.scaleY,
                rotation: restAngle, opacity: 1, duration: 0.6, ease: EASE_SETTLE, ...GPU,
                onComplete: () => { this.isAnimating = false; this.afterFinish(); },
            }
        );
    }

    afterFinish() {
        this.props.XY({ index: -1, text: this.props.text, x: this.props.x, y: this.props.y });
    }

    setNewShuffleCard(data) {
        this.setState({ cardFrame: safeFrame(data) });
    }

    SuffleCard(data, cardsLength, cardXYA, num) {
        if (!this.cardRef) return;
        this.isAnimating = true;
        gsap.killTweensOf(this.cardRef);
        const restAngle = this.props.angled || 0;
        if (cardsLength === 5) {
            gsap.fromTo(this.cardRef, { x: data.x, y: data.y - 14, opacity: 0.5, rotation: restAngle }, {
                x: this.props.x, y: this.props.y, rotation: restAngle, duration: 0.45, ease: EASE_SETTLE, opacity: 1, ...GPU,
                onStart: () => this.playSound(this.cardsshuffleSound),
                onComplete: () => { this.isAnimating = false; this.Uc(data); },
            });
        } else if (cardXYA) {
            gsap.fromTo(this.cardRef, { x: data.x, y: data.y - 14, opacity: 0.5, rotation: restAngle }, {
                x: cardXYA.x, y: cardXYA.y, rotation: restAngle, duration: 0.45, ease: EASE_SETTLE, opacity: 1, ...GPU,
                onStart: () => this.playSound(this.cardsshuffleSound),
                onComplete: () => { this.isAnimating = false; this.Uc(cardXYA); },
            });
        }
    }

    Uc(data) {
        this.props.XY({ index: this.props.index, text: this.props.text, x: data.x, y: data.y });
    }

    DrawanimateCard(data) {
        if (!this.cardRef) return;
        this.isAnimating = true;
        gsap.killTweensOf(this.cardRef);
        // Player cards always show face value while falling — no
        // flip-to-back-then-flip-to-face like the dealer's mask reveal.
        this.setState({ cardFrame: safeFrame(this.props.frame) });

        // Buy/draw exchange: card shrinks straight back to the deck and
        // grows straight back into its seat — no spin. `restAngle` is the
        // card's resting tilt in the arch/fan, held fixed through the
        // whole timeline (instead of animating 8deg -> -8deg -> 0) so the
        // card never rotates independently of the fan it belongs to.
        const restAngle = this.props.angled || 0;

        const tl = gsap.timeline({ onComplete: () => { this.isAnimating = false; this.draw(); } });
        tl.fromTo(this.cardRef, { x: this.props.x, y: this.props.y, scaleX: 1, scaleY: 1, opacity: 1, rotation: restAngle }, {
            x: this.cardDropPositionX, y: this.cardDropPositionY, scaleX: 0.7, scaleY: 0.5, rotation: restAngle, duration: 0.4,
            ease: EASE_LIFT, opacity: 0, ...GPU,
            onStart: () => this.playSound(this.carddropsound),
        });
        tl.fromTo(this.cardRef, { x: this.cardDropPositionX, y: this.cardDropPositionY, scaleX: 0.2, scaleY: 0.2, opacity: 0, rotation: restAngle }, {
            x: this.props.x, y: this.props.y, scaleX: this.props.scaleX, scaleY: this.props.scaleY, rotation: restAngle, duration: 0.45,
            ease: EASE_SETTLE, opacity: 1, ...GPU,
            onStart: () => this.playSound(this.carddropsound),
        }, "-=0.03");
    }

    draw() {
        this.props.XY({ index: this.props.index, text: this.props.text, x: this.props.x, y: this.props.y });
    }

    moveCard(data) {
        if (!this.cardRef) return;
        gsap.killTweensOf(this.cardRef);
        this.setVisible(true);
        gsap.to(this.cardRef, {
            x: data.x, y: data.y, opacity: 1, duration: 0.45, ease: EASE_FLIGHT, rotation: data.angle, ...GPU,
            onComplete: () => this.MoveFInished(data),
        });
    }

    MoveFInished(data) {
        this.props.XY({ index: this.props.index, text: this.props.text, x: data.x, y: data.y });
    }

    loadImage(img) {
        this.image = new window.Image();
        this.image.src = img;
        this.image.addEventListener("load", this.handleLoad);
    }

    handleLoad = () => {
        this.setState({
            sheetW: this.image.naturalWidth,
            sheetH: this.image.naturalHeight,
            ready: true,
        });
    };

    setVisible(v) {
        this.setState({ visible: v });
    }

    // Single entry point for both mouse clicks and touch taps. Only
    // `onClick` is wired up in render() now — see the comment there for
    // why `onTouchEnd` was removed instead of just calling the same
    // handler from both events.
    CardPullUP(e) {
        const { selectCardToBeRemove, selectCardToBeUnRemove } = this.props;
        if (this.cardRef) gsap.killTweensOf(this.cardRef);
        this.setState(prev => {
            const selected = !prev.selected;
            if (selected) {
                gsap.to(this.cardRef, { y: this.props.y - SELECT_LIFT_Y, opacity: SELECTED_OPACITY, duration: 0.25, ease: EASE_SETTLE, ...GPU });
                selectCardToBeRemove(this.props.text, this.props.index);
                this.playSound(this.selectCard);
            } else {
                gsap.to(this.cardRef, { y: this.props.y, opacity: this.props.alpha, duration: 0.25, ease: EASE_FLIGHT, ...GPU });
                selectCardToBeUnRemove(this.props.text, this.props.index);
                this.playSound(this.unselectCard);
            }
            return { selected };
        });
    }

    unselectPokerCards() {
        if (!this.cardRef) return;
        gsap.killTweensOf(this.cardRef);
        gsap.to(this.cardRef, { y: this.props.y, opacity: this.props.alpha, duration: 0.25, ease: EASE_FLIGHT, ...GPU });
        this.playSound(this.unselectCard);
        this.setState({ selected: false });
    }

    endGame = () => {
        if (this.cardContext) {
            this.cardContext.revert();
        }
        // A fresh hand (ReBet) must not carry over a previous hand's draw
        // selection — without this the lift offset AND the dulled opacity
        // would silently persist onto card index 0 of the next deal, since
        // neither is reset anywhere else.
        this.setState({ selected: false });
    };

    render() {
        const { cardFrame, sheetW, sheetH, ready, visible, selected } = this.state;
        const [fx, fy, fw, fh] = safeFrame(cardFrame);

        const style = {
            position: "absolute",
            top: 0,
            left: 0,
            width: fw,
            height: fh,
            transformOrigin: "50% 50%",
            // Selected-for-draw feedback is just a dulled opacity — set here
            // (not only in the CardPullUP tween) so it survives every later
            // changeCardPosition/handleResize re-render instead of getting
            // silently overwritten back to full opacity the next time the
            // hand re-lays itself out (new card dealt, resize, orientation
            // change).
            opacity: ready ? (selected ? SELECTED_OPACITY : this.props.alpha) : 0,
            visibility: visible ? "visible" : "hidden",
            backgroundImage: ready ? `url(${this.cardStyle})` : "none",
            backgroundPosition: `-${fx}px -${fy}px`,
            backgroundSize: `${sheetW}px ${sheetH}px`,
            backgroundRepeat: "no-repeat",
            willChange: "transform, opacity",
            pointerEvents: "auto",
            cursor: "pointer",
            // `touchAction: manipulation` tells the browser to skip the
            // double-tap-to-zoom delay/gesture detection on this element,
            // so a plain `onClick` fires immediately for a tap — no need
            // for a parallel `onTouchEnd` handler (which was firing
            // CardPullUP a second time right after touchend on mobile,
            // instantly undoing the first toggle — the actual cause of
            // "tapping the card doesn't do anything" in portrait AND
            // landscape).
            touchAction: "manipulation",
            // NOTE: intentionally no `transform` key here. GSAP owns this
            // node's transform exclusively (see setCardRef in the
            // constructor for the one-time initial value). Adding it back
            // here will cause every unrelated re-render to silently reset
            // the card's real position/scale out from under GSAP.
        };

        return (
            <div
                className={`rp-card${selected ? " rp-card--selected" : ""}`}
                ref={this.setCardRef}
                style={style}
                onClick={(e) => this.CardPullUP(e)}
            />
        );
    }
}